import React, { useState, useEffect } from 'react';
import { MessageCircle, CheckCircle, XCircle, ExternalLink, Copy, Phone } from 'lucide-react';

const WhatsAppSetup = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const webhookUrl = `${API_URL}/api/whatsapp/webhook`;

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/status`);
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error('Error fetching WhatsApp status:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const isConfigured = status?.twilio_configured;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="whatsapp-setup">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-green-100 rounded-lg">
          <MessageCircle className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">WhatsApp AI Chatbot</h2>
          <p className="text-sm text-gray-500">24/7 AI-powered investment advice via WhatsApp</p>
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex items-center gap-2 mb-6">
        {isConfigured ? (
          <>
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-green-700 font-medium">Connected & Active</span>
          </>
        ) : (
          <>
            <XCircle className="w-5 h-5 text-amber-500" />
            <span className="text-amber-700 font-medium">Pending Twilio Configuration</span>
          </>
        )}
      </div>

      {/* Webhook URL */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <label className="text-sm font-medium text-gray-700 mb-2 block">Webhook URL</label>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-800 overflow-x-auto">
            {webhookUrl}
          </code>
          <button
            onClick={copyWebhookUrl}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            title="Copy webhook URL"
            data-testid="copy-webhook-btn"
          >
            <Copy className={`w-5 h-5 ${copied ? 'text-green-500' : 'text-gray-500'}`} />
          </button>
        </div>
        {copied && <p className="text-green-600 text-xs mt-1">Copied to clipboard!</p>}
      </div>

      {/* Setup Instructions */}
      {!isConfigured && (
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900">Setup Instructions</h3>
          <ol className="space-y-3 text-sm text-gray-600">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-medium">1</span>
              <span>Create a Twilio account at <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline inline-flex items-center gap-1">twilio.com <ExternalLink className="w-3 h-3" /></a></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-medium">2</span>
              <span>Get your Account SID and Auth Token from the Twilio Console Dashboard</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-medium">3</span>
              <span>Set up WhatsApp Sandbox (for testing) or apply for a WhatsApp Business number</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-medium">4</span>
              <span>Configure the webhook URL above in Twilio's WhatsApp settings</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-medium">5</span>
              <span>Add credentials to your environment variables (backend/.env)</span>
            </li>
          </ol>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> The AI chatbot is ready to respond! Once you configure Twilio credentials, 
              users can message your WhatsApp number and receive instant AI-powered investment advice in English or Swahili.
            </p>
          </div>
        </div>
      )}

      {/* Features */}
      <div className="mt-6 pt-6 border-t border-gray-100">
        <h3 className="font-medium text-gray-900 mb-3">Chatbot Features</h3>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            AI-powered investment advice
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            Swahili & English support
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            DSE stock information
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            UTT & Treasury guidance
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            24/7 availability
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            Session memory
          </li>
        </ul>
      </div>
    </div>
  );
};

export default WhatsAppSetup;
